import {
  Alert,
  App,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Upload,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { uploadAttachment } from '@/services/bff/attachment.api';
import {
  getReceiptContext,
  getReceiptContracts,
  receiptPlanAction,
  registrationAction,
  saveReceiptPlan,
  saveRegistration,
} from '@/services/bff/receipt-workflow.api';
import type {
  ReceiptContractContext,
  ReceiptPlan,
  ReceiptRegistration,
  SaveReceiptPlan,
  SaveReceiptRegistration,
} from '@/services/bff/types/receipt-workflow';
import { BusinessModal } from '../business-modal';
import { validateAllocation } from './receipt-money';
import styles from './style.module.less';
import type { ReceiptCommand } from './use-receipt-command';

type Props = {
  kind: 'plan' | 'registration';
  contractUuid?: string;
  customerUuid?: string;
  planUuid?: string;
  record?: ReceiptPlan | ReceiptRegistration;
  command: ReceiptCommand;
  canSubmit: boolean;
  canEnable?: boolean;
  contextVersion?: number;
  onCreatePlan?: (contractUuid: string) => void;
  onClose: () => void;
  onSaved: () => void;
};
export function ReceiptForm({
  kind,
  contractUuid,
  customerUuid,
  planUuid,
  record,
  command,
  canSubmit,
  canEnable,
  contextVersion,
  onCreatePlan,
  onClose,
  onSaved,
}: Props) {
  const { modal, message } = App.useApp();
  const [form] = Form.useForm();
  const [contracts, setContracts] = useState<
    { value: string; label: string }[]
  >([]);
  const [context, setContext] = useState<ReceiptContractContext>();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const dirty = useRef(false);
  const initializedContract = useRef<string | undefined>(undefined);
  const saved = useRef(record as ReceiptRegistration | undefined);
  const uuid = Form.useWatch('contractUuid', form) as string | undefined;
  const policy = Form.useWatch('recognitionPolicy', form) as string | undefined;
  const method = Form.useWatch('receiptMethod', form) as string | undefined;
  const vouchers = (Form.useWatch('voucherAttachmentUuids', form) ||
    []) as string[];
  const searchSequence = useRef(0);
  const searchContracts = async (keyword = '') => {
    const sequence = ++searchSequence.current;
    try {
      const page = await getReceiptContracts({
        keyword,
        customerUuid,
        scope: 'all',
        pageSize: 50,
      });
      if (sequence === searchSequence.current)
        setContracts(
          page.items.map((item) => ({
            value: item.uuid,
            label: `${item.contractNo} · ${item.contractName}`,
          })),
        );
    } catch {
      if (sequence === searchSequence.current)
        setError('合同加载失败，请重新搜索');
    }
  };
  useEffect(() => {
    form.setFieldsValue({
      sequenceNo: 1,
      receiptMethod: 'BANK_TRANSFER',
      recognitionPolicy: 'MANUAL',
      voucherAttachmentUuids: [],
      allocations: [],
      ...record,
      contractUuid: record?.contractUuid || contractUuid,
      receiptDate:
        'receiptDate' in (record || {})
          ? dayjs((record as ReceiptRegistration).receiptDate)
          : dayjs(),
      plannedDate: (record as ReceiptPlan)?.plannedDate
        ? dayjs((record as ReceiptPlan).plannedDate)
        : undefined,
      dueDate: (record as ReceiptPlan)?.dueDate
        ? dayjs((record as ReceiptPlan).dueDate)
        : undefined,
    });
    void searchContracts();
  }, []);
  useEffect(() => {
    if (!uuid) {
      setContext(undefined);
      return;
    }
    let active = true;
    setLoading(true);
    setError('');
    setContext(undefined);
    getReceiptContext(uuid)
      .then((value) => {
        if (!active) return;
        setContext(value);
        if (!record && !saved.current && initializedContract.current !== uuid)
          form.setFieldsValue({
            currency: value.currency,
            payerName: value.customerName,
            accountUuid: undefined,
            financialSubjectUuid: undefined,
            counterpartyPartyUuid: undefined,
            voucherAttachmentUuids: [],
            allocations: planUuid ? [{ planUuid, amount: undefined }] : [],
          });
        initializedContract.current = uuid;
      })
      .catch((cause) => {
        if (active)
          setError(
            cause instanceof Error ? cause.message : '合同回款信息加载失败',
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [uuid, contextVersion]);
  const close = () => {
    if (command.busy || uploading) return;
    if (!dirty.current) {
      onClose();
      return;
    }
    modal.confirm({
      title: '放弃未保存的修改？',
      content: command.blocked
        ? '请求编号已保留，可在回款页面查询处理结果。'
        : '关闭后未保存的表单内容将丢失。',
      closable: { 'aria-label': '关闭' },
      keyboard: true,
      okText: '放弃修改',
      cancelText: '取消',
      onOk: onClose,
    });
  };
  const submit = async (toFinance: boolean) => {
    setError('');
    try {
      const values = await form.validateFields();
      if (!context || !uuid) throw new Error('请先选择可操作合同');
      if (kind === 'plan') {
        const data = {
          ...values,
          plannedAmount: String(values.plannedAmount),
          revision: saved.current?.revision || record?.revision,
          plannedDate: values.plannedDate?.format('YYYY-MM-DD'),
          dueDate: values.dueDate.format('YYYY-MM-DD'),
        } as SaveReceiptPlan;
        const result = await command.execute('保存回款计划', 'contract', (id) =>
          saveReceiptPlan(
            { ...data, clientRequestId: id },
            saved.current?.uuid || record?.uuid,
          ),
        );
        saved.current = result as unknown as ReceiptRegistration;
        if (toFinance)
          await command.execute('启用回款计划', 'contract', (id) =>
            receiptPlanAction(result.uuid, 'enable', result.revision, id),
          );
      } else {
        const data = {
          ...values,
          amount: String(values.amount),
          allocations: values.allocations.map(
            (item: { planUuid: string; amount: string }) => ({
              ...item,
              amount: String(item.amount),
            }),
          ),
          receiptDate: values.receiptDate.format('YYYY-MM-DD'),
          revision: saved.current?.revision,
          voucherAttachmentUuids: values.voucherAttachmentUuids || [],
        } as SaveReceiptRegistration;
        validateAllocation(data.amount, data.allocations);
        if (toFinance) {
          if (!data.voucherAttachmentUuids.length)
            throw new Error('提交财务必须上传凭证');
          if (
            data.receiptMethod === 'BANK_TRANSFER' &&
            !data.bankSerialNo?.trim()
          )
            throw new Error('银行转账必须填写流水号');
          if (
            data.payerName.trim() !== context.customerName.trim() &&
            !data.payerDifferenceReason?.trim()
          )
            throw new Error('付款方与客户不一致，请填写说明');
        }
        const result = await command.execute('保存回款登记', 'finance', (id) =>
          saveRegistration(
            { ...data, clientRequestId: id },
            saved.current?.uuid,
          ),
        );
        if (!result?.uuid || !Number.isInteger(result.revision))
          throw new Error('登记返回信息不完整，请查询请求结果');
        saved.current = result;
        if (toFinance)
          await command.execute('提交财务确认', 'finance', (id) =>
            registrationAction(result.uuid, 'submit', result.revision, id),
          );
      }
      dirty.current = false;
      message.success(
        toFinance
          ? kind === 'plan'
            ? '计划已启用'
            : '已提交财务确认'
          : '保存成功',
      );
      onSaved();
    } catch (cause) {
      if (cause instanceof Error) setError(cause.message);
    }
  };
  return (
    <BusinessModal
      open
      title={
        kind === 'plan'
          ? record
            ? '编辑回款计划'
            : '新增回款计划'
          : record
            ? '编辑回款登记'
            : '发起回款'
      }
      widthPreset="wideForm"
      onCancel={close}
      mask={{ closable: false }}
      footer={
        <div className={styles.actions}>
          <Button disabled={command.busy || uploading} onClick={close}>
            取消
          </Button>
          <Button
            disabled={!context || loading || uploading || command.blocked}
            loading={command.busy}
            onClick={() => void submit(false)}
          >
            保存草稿
          </Button>
          {kind === 'plan' && (
            <Button
              type="primary"
              disabled={
                !canEnable ||
                context?.settlementAmountPolicy === 'EXACT' ||
                !context ||
                loading ||
                uploading ||
                command.blocked
              }
              loading={command.busy}
              onClick={() => void submit(true)}
            >
              保存并启用
            </Button>
          )}
          {kind === 'registration' && (
            <Button
              type="primary"
              disabled={
                !canSubmit ||
                !context ||
                loading ||
                uploading ||
                command.blocked
              }
              loading={command.busy}
              onClick={() => void submit(true)}
            >
              提交财务
            </Button>
          )}
        </div>
      }
    >
      <Space orientation="vertical" style={{ width: '100%' }}>
        {command.feedback}
        {error && <Alert type="error" showIcon title={error} />}
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            className={styles.form}
            disabled={command.busy || command.blocked || uploading}
            onValuesChange={() => {
              dirty.current = true;
            }}
          >
            <div className={styles.fields}>
              <Form.Item
                label="合同"
                name="contractUuid"
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  filterOption={false}
                  disabled={Boolean(contractUuid || record || saved.current)}
                  options={contracts}
                  onSearch={(keyword) => void searchContracts(keyword)}
                />
              </Form.Item>
              <Form.Item
                label="币种"
                name="currency"
                rules={[{ required: true }]}
              >
                <Input readOnly />
              </Form.Item>
              {kind === 'plan' ? (
                <>
                  <Form.Item
                    label="计划名称"
                    name="planName"
                    rules={[{ required: true, whitespace: true }]}
                  >
                    <Input maxLength={200} />
                  </Form.Item>
                  <Form.Item
                    label="期次"
                    name="sequenceNo"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      min={1}
                      max={9999}
                      precision={0}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="计划金额"
                    name="plannedAmount"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      stringMode
                      min="0.000001"
                      precision={6}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item label="计划日期" name="plannedDate">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    label="到期日期"
                    name="dueDate"
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    label="财务主体"
                    name="financialSubjectUuid"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={context?.financialSubjects.map((item) => ({
                        value: item.uuid,
                        label: item.name,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item
                    label="付款方"
                    name="counterpartyPartyUuid"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={context?.counterparties.map((item) => ({
                        value: item.uuid,
                        label: item.name,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item
                    label="确认条件"
                    name="recognitionPolicy"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={[
                        { value: 'MANUAL', label: '手动确认' },
                        { value: 'ON_DATE', label: '按日期' },
                        { value: 'ON_CONTRACT_ACTIVE', label: '合同生效' },
                        { value: 'ON_FULFILLMENT', label: '履约完成' },
                      ]}
                    />
                  </Form.Item>
                  {policy === 'ON_FULFILLMENT' && (
                    <Form.Item
                      label="履约事项"
                      name="fulfillmentUuid"
                      rules={[{ required: true }]}
                    >
                      <Select
                        options={context?.fulfillments.map((item) => ({
                          value: item.uuid,
                          label: item.name,
                        }))}
                      />
                    </Form.Item>
                  )}
                  <div className={styles.wide}>
                    <Alert
                      type="info"
                      showIcon
                      title={`计划金额策略：${context?.settlementAmountPolicy === 'EXACT' ? '全部计划启用时合计须等于合同金额' : '计划合计不得超过合同金额'}；启用不代表财务已确认应收。`}
                    />
                  </div>
                </>
              ) : (
                <>
                  <Form.Item
                    label="到账日期"
                    name="receiptDate"
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    label="回款金额"
                    name="amount"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      stringMode
                      min="0.000001"
                      precision={6}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="付款方"
                    name="payerName"
                    rules={[{ required: true, whitespace: true }]}
                  >
                    <Input maxLength={200} />
                  </Form.Item>
                  <Form.Item
                    label="付款方式"
                    name="receiptMethod"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={[
                        { value: 'BANK_TRANSFER', label: '银行转账' },
                        { value: 'CASH', label: '现金' },
                        { value: 'CHECK', label: '支票' },
                        { value: 'OTHER', label: '其他' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    label="收款账户"
                    name="accountUuid"
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={context?.accounts.map((item) => ({
                        value: item.uuid,
                        label: item.name,
                      }))}
                      onChange={(value) =>
                        form.setFieldValue(
                          'financialSubjectUuid',
                          context?.accounts.find((item) => item.uuid === value)
                            ?.financialSubjectUuid,
                        )
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    label="收款主体"
                    name="financialSubjectUuid"
                    rules={[{ required: true }]}
                  >
                    <Select
                      disabled
                      options={context?.financialSubjects.map((item) => ({
                        value: item.uuid,
                        label: item.name,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item
                    label={`流水号${method === 'BANK_TRANSFER' ? '（提交必填）' : ''}`}
                    name="bankSerialNo"
                  >
                    <Input maxLength={200} />
                  </Form.Item>
                  <Form.Item
                    label="付款方差异说明"
                    name="payerDifferenceReason"
                    className={styles.wide}
                  >
                    <Input.TextArea
                      maxLength={1000}
                      autoSize={{ minRows: 2, maxRows: 4 }}
                    />
                  </Form.Item>
                  <div className={styles.wide}>
                    <Form.List name="allocations">
                      {(fields, { add, remove }) => (
                        <Space orientation="vertical" style={{ width: '100%' }}>
                          <strong>计划分配</strong>
                          {fields.map((field) => (
                            <div key={field.key} className={styles.allocations}>
                              <Form.Item
                                name={[field.name, 'planUuid']}
                                rules={[
                                  { required: true, message: '请选择计划' },
                                ]}
                              >
                                <Select
                                  placeholder="选择已启用的计划"
                                  options={context?.plans
                                    .filter((item) =>
                                      ['READY', 'RECOGNIZED'].includes(
                                        item.status,
                                      ),
                                    )
                                    .map((item) => ({
                                      value: item.uuid,
                                      label: `${item.planName} · 可登记 ${item.availableAmount ?? '待同步'}`,
                                    }))}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, 'amount']}
                                rules={[
                                  { required: true, message: '填写分配金额' },
                                ]}
                              >
                                <InputNumber
                                  aria-label="分配金额"
                                  stringMode
                                  min="0.000001"
                                  precision={6}
                                  style={{ width: '100%' }}
                                />
                              </Form.Item>
                              <Button onClick={() => remove(field.name)}>
                                移除
                              </Button>
                            </div>
                          ))}
                          <Button onClick={() => add()}>添加计划分配</Button>
                          {context &&
                            !context.plans.some((item) =>
                              ['READY', 'RECOGNIZED'].includes(item.status),
                            ) && (
                              <Alert
                                type="info"
                                title="没有可登记的计划，请先创建并启用计划。"
                                action={
                                  onCreatePlan && (
                                    <Button onClick={() => onCreatePlan(uuid!)}>
                                      快捷创建计划
                                    </Button>
                                  )
                                }
                              />
                            )}
                        </Space>
                      )}
                    </Form.List>
                  </div>
                  <Form.Item name="voucherAttachmentUuids" hidden>
                    <Input />
                  </Form.Item>
                  <div className={styles.wide}>
                    <Space wrap>
                      <Upload
                        showUploadList={false}
                        beforeUpload={async (file) => {
                          if (!uuid || uploading) return false;
                          setUploading(true);
                          try {
                            const result = await uploadAttachment({
                              file,
                              bizType: 'receipt-voucher',
                              bizUuid: uuid,
                            });
                            form.setFieldValue('voucherAttachmentUuids', [
                              ...(form.getFieldValue(
                                'voucherAttachmentUuids',
                              ) || []),
                              result.uuid,
                            ]);
                            dirty.current = true;
                          } catch {
                            setError('凭证上传失败，请重试');
                          } finally {
                            setUploading(false);
                          }
                          return false;
                        }}
                      >
                        <Button
                          disabled={
                            !context ||
                            command.busy ||
                            command.blocked ||
                            uploading
                          }
                          loading={uploading}
                        >
                          上传回款凭证
                        </Button>
                      </Upload>
                      <span>已选择 {vouchers.length} 份凭证（提交必填）</span>
                      {vouchers.map((value, index) => (
                        <Button
                          key={value}
                          disabled={command.blocked}
                          onClick={() => {
                            form.setFieldValue(
                              'voucherAttachmentUuids',
                              vouchers.filter((item) => item !== value),
                            );
                            dirty.current = true;
                          }}
                        >
                          移除凭证 {index + 1}
                        </Button>
                      ))}
                    </Space>
                  </div>
                </>
              )}
              <Form.Item label="备注" name="remark" className={styles.wide}>
                <Input.TextArea
                  maxLength={2000}
                  autoSize={{ minRows: 2, maxRows: 5 }}
                />
              </Form.Item>
            </div>
          </Form>
        </Spin>
      </Space>
    </BusinessModal>
  );
}
